import React from 'react';
import { Box, Typography, Button, Chip } from '@material-ui/core';
import { Table, TableColumn } from '@backstage/core-components';
import AddIcon from '@material-ui/icons/Add';
import { Backup, DatabaseCluster, StorageLocation } from '../../types';

interface BackupsViewProps {
  backups: Backup[];
  clusters: DatabaseCluster[];
  storageLocations: StorageLocation[];
  onRefresh: () => void;
}

const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

const getStatusColor = (
  status?: string,
): 'default' | 'primary' | 'secondary' => {
  switch (status) {
    case 'succeeded':
      return 'primary';
    case 'failed':
      return 'secondary';
    default:
      return 'default';
  }
};

export const BackupsView: React.FC<BackupsViewProps> = ({
  backups,
  onRefresh,
}) => {
  const columns: TableColumn[] = [
    {
      title: 'Name',
      field: 'metadata.name',
      highlight: true,
    },
    {
      title: 'Database Cluster',
      field: 'spec.dbClusterName',
    },
    {
      title: 'Namespace',
      field: 'metadata.namespace',
    },
    {
      title: 'Status',
      field: 'status.state',
      render: (row: any) => (
        <Chip
          label={row.status?.state || 'unknown'}
          color={getStatusColor(row.status?.state)}
          size="small"
        />
      ),
    },
    {
      title: 'Size',
      field: 'status.size',
      render: (row: any) =>
        row.status?.size ? formatBytes(row.status.size) : 'N/A',
    },
    {
      title: 'Storage',
      field: 'spec.backupStorageName',
    },
    {
      title: 'Created',
      field: 'metadata.creationTimestamp',
      render: (row: any) =>
        row.metadata.creationTimestamp
          ? new Date(row.metadata.creationTimestamp).toLocaleString()
          : 'N/A',
    },
    {
      title: 'Completed',
      field: 'status.completed',
      render: (row: any) =>
        row.status?.completed
          ? new Date(row.status.completed).toLocaleString()
          : 'N/A',
    },
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">Backups</Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={onRefresh}
        >
          Create On-Demand Backup
        </Button>
      </Box>
      <Table
        title="All Backups"
        options={{
          paging: true,
          pageSize: 10,
          search: true,
          sorting: true,
        }}
        columns={columns}
        data={backups}
      />
    </Box>
  );
};
